import { createFeatureSelector, createSelector } from "@ngrx/store";
import { isNotNullOrUndefined } from "@pcp/core/util";
import { ActiveLessonInfo, ClassroomState } from "./classroomState";
import { lessonFeatureKey } from "../../internal/store/lesson-reducer";
import { guid } from "@pcp/core/interfaces";

export const selectLessonState = createFeatureSelector<ClassroomState>(lessonFeatureKey);

export const selectActiveLesson = createSelector(
    selectLessonState,
    (state): undefined | ActiveLessonInfo => state.activeLesson
);

export const selectLessonId = createSelector(
    selectActiveLesson,
    (activeLesson): undefined | guid => activeLesson?.lessonId
);

export const selectLiveLessonId = createSelector(
    selectLessonState,
    state => state.activeLesson?.lessonId
);

// `selectLessonIdOrLiveLessonId` historically combined two selectors that both
// returned `state.activeLesson?.lessonId`. Kept as an alias on `selectLessonId`
// since external callers (4) reference this name.
export const selectLessonIdOrLiveLessonId = selectLessonId;

export const isLessonStarted = createSelector(
    selectLessonState,
    (state) => state.started === true
);

export const isLessonPaused = createSelector(
    selectLessonState,
    (state) => state.paused === true
);

export const isLoadingLessoncode = createSelector(
    selectLessonState,
    (state) => state.isLoadingLessonCode === true
);

export const isBeamerScreenOpen = createSelector(selectLessonState, (state) =>
    isNotNullOrUndefined(state.openedOnWindowId)
);

export const selectLessonButtonState = createSelector(
    isLoadingLessoncode,
    isLessonStarted,
    isLessonPaused,
    (isLoading, isStarted, isPaused) => {
        if (isLoading === true) return 'loading';
        if (isStarted !== true) return 'stopped';
        if (isPaused) return 'paused';
        return 'started';
    }
);

export const selectStudentRegistrationCode = createSelector(
    selectActiveLesson,
    (activeLesson) => activeLesson?.studentRegistrationCode
);

export const selectIsShowingStudentRegistrationCode = createSelector(
    selectLessonState,
    (state) => state.isShowingStudentRegistrationCode === true
);

export const selectIsLessonStopPending = createSelector(
    selectLessonState,
    (state) => state.isLessonStopPending === true
);

export const selectStartedAt = createSelector(
    selectLessonState,
    (state) => state.startedAt
);

export const selectPresentationId = createSelector(
    selectActiveLesson,
    (activeLesson) => activeLesson?.presentationId
);

export const selectIsPresentationStarted = createSelector(
    selectPresentationId,
    (presentationId) => presentationId !== undefined && presentationId !== null
);

export const selectPauseImageUrl = createSelector(
    selectLessonState,
    (state) => state.pauseImageUrl ?? null
);

/**
 * Whether the currently active basic-slide list has a parent lection/chapter to
 * navigate up to. Derived from the Yjs-mirrored `navigationPreviewBasicSlides` JSON
 * by `lesson-reducer`. Used by toolbar consumers to gate the "go to parent slide" button.
 */
export const selectNavigationPreviewHasParent = createSelector(
    selectLessonState,
    (state) => state.navigationPreviewHasParent
);
